import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { browseRecords } from "../../api/adminApi";

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { value: "desc", labelKey: "admin.browse.sortLatestFirst" },
  { value: "asc",  labelKey: "admin.browse.sortOldestFirst" },
  { value: "random", labelKey: "admin.browse.sortRandom" },
];

export default function BrowseRecordsPanel({ database, token }) {
  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState("desc");
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState(null); // { items, totalCount, offset, pageSize }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const dbId = database?.id ?? database?.Id ?? database?.dbId ?? null;

  const load = useCallback(async (newOffset, newSort) => {
    if (!dbId) return;
    setLoading(true);
    setError("");
    try {
      const res = await browseRecords(token, {
        databaseId: dbId,
        offset: newOffset,
        pageSize: PAGE_SIZE,
        sortOrder: newSort,
      });
      setResult(res);
      setLoaded(true);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [dbId, token]);

  function handleLoad() {
    setOffset(0);
    load(0, sortOrder);
  }

  function handleSortChange(e) {
    const newSort = e.target.value;
    setSortOrder(newSort);
    if (loaded) {
      setOffset(0);
      load(0, newSort);
    }
  }

  function handlePrev() {
    const newOffset = Math.max(0, offset - PAGE_SIZE);
    setOffset(newOffset);
    load(newOffset, sortOrder);
  }

  function handleNext() {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    load(newOffset, sortOrder);
  }

  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, totalCount);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < totalCount;

  // Build column headers from indexes + fixed columns
  const fixedCols = ["Id", "ArchiveNo", "DocumentDate", "OriginalFileName", "FileExtension", "PageCount"];
  const extraCols = items.length > 0
    ? Object.keys(items[0].searchIndexes ?? {})
    : [];
  const allCols = [...fixedCols, ...extraCols];

  function cellValue(item, col) {
    switch (col) {
      case "Id": return item.id ?? item.Id;
      case "ArchiveNo": return item.customerNo ?? item.CustomerNo;
      case "DocumentDate": {
        const d = item.documentDate ?? item.DocumentDate;
        if (!d || d === "0001-01-01T00:00:00") return "—";
        try { return new Date(d).toLocaleDateString(); } catch { return d; }
      }
      case "OriginalFileName": return item.originalName ?? item.OriginalName;
      case "FileExtension": return item.fileExtension ?? item.FileExtension;
      case "PageCount": return item.pageCount ?? item.PageCount;
      default: return item.searchIndexes?.[col] ?? "—";
    }
  }

  return (
    <div className="browse-records-panel">
      {/* Controls row */}
      <div className="browse-controls">
        <select
          className="browse-sort-select"
          value={sortOrder}
          onChange={handleSortChange}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>

        {!loaded && (
          <button className="chip-btn browse-load-btn" onClick={handleLoad} disabled={loading || !dbId}>
            {loading ? t("admin.browse.loading") : t("admin.browse.load")}
          </button>
        )}
      </div>

      {error && (
        <div className="browse-error">{error}</div>
      )}

      {loading && (
        <div className="browse-loading">{t("admin.browse.loading")}</div>
      )}

      {loaded && !loading && (
        <>
          {/* Pagination info + nav */}
          <div className="browse-pagination-bar">
            <span className="browse-count-label">
              {totalCount === 0
                ? t("admin.browse.noRecords")
                : t("admin.browse.showing", { from, to, total: totalCount.toLocaleString() })}
            </span>
            <div className="browse-nav-buttons">
              <button
                className="chip-btn"
                onClick={handlePrev}
                disabled={!hasPrev || loading}
              >
                ← {t("admin.browse.prev")}
              </button>
              <button
                className="chip-btn"
                onClick={handleNext}
                disabled={!hasNext || loading}
              >
                {t("admin.browse.next")} →
              </button>
            </div>
          </div>

          {/* Records table */}
          {items.length === 0 ? (
            <div className="browse-empty">{t("admin.browse.noRecords")}</div>
          ) : (
            <div className="browse-table-wrap">
              <table className="browse-table">
                <thead>
                  <tr>
                    {allCols.map(col => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id ?? item.Id ?? idx}>
                      {allCols.map(col => (
                        <td key={col}>{cellValue(item, col)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bottom pagination nav */}
          {items.length > 10 && (
            <div className="browse-pagination-bar browse-pagination-bar--bottom">
              <span className="browse-count-label">
                {t("admin.browse.showing", { from, to, total: totalCount.toLocaleString() })}
              </span>
              <div className="browse-nav-buttons">
                <button className="chip-btn" onClick={handlePrev} disabled={!hasPrev || loading}>
                  ← {t("admin.browse.prev")}
                </button>
                <button className="chip-btn" onClick={handleNext} disabled={!hasNext || loading}>
                  {t("admin.browse.next")} →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
